import { useMemo } from 'react';
import { mapObjIndexed, pick, prop } from 'ramda';
import { required, range, oneOfBy, int, ascending } from '@core/js/utils/Validation/validationRules';
import { isNonZero, isNotEmpty } from '@core/js/utils/Validation/validationMethods';
import { usePortletSelector } from '@core/js/components/system/Portlet';
import { LIMIT_FIELD_NAMES, LIMIT_TYPES, UNLIMITED } from '@core/js/components/main/ResponsibleGaming/LoginLimits/constants';

const { LOGIN_COUNT, DAILY_LIMIT, WEEKLY_LIMIT, MONTHLY_LIMIT } = LIMIT_TYPES;
const loginCountName = LIMIT_FIELD_NAMES[LOGIN_COUNT];
const dailyName = LIMIT_FIELD_NAMES[DAILY_LIMIT];
const weeklyName = LIMIT_FIELD_NAMES[WEEKLY_LIMIT];
const monthlyName = LIMIT_FIELD_NAMES[MONTHLY_LIMIT];
const timeLimits = [dailyName, weeklyName, monthlyName];

const allowedLimits = (limits = []) => config => limits.filter(item => config[item]);
const getAllowedTimeLimits = allowedLimits(timeLimits);
const getAllowedLimits = allowedLimits([loginCountName, ...timeLimits]);

const overEvery = checks => item => checks.every(check => check(item));

const isNotEmptyLimit = overEvery([isNotEmpty, isNonZero]);

const getDefaultRules = getTKey => ({
    [loginCountName]: () => [
        required({ predicate: isNotEmptyLimit, error: getTKey('loginCountRequired') }),
        int({ error: getTKey('onlyDigits') }),
        range({ min: 0, error: getTKey('onlyDigits') }),
        range({ max: Number.MAX_SAFE_INTEGER, exceptions: [UNLIMITED], error: getTKey('maxExceeded') })
    ],
    [dailyName]: fields => [
        ascending({ fields, error: getTKey('daily') })
    ],
    [weeklyName]: fields => [
        ascending({ fields, error: getTKey('weekly') })
    ],
    [monthlyName]: fields => [
        ascending({ fields, error: getTKey('monthly') })
    ]
});

export const useValidationConfig = () => {
    const tKeyPrefix = 'playtech.mobile.system.loginLimits';
    const config = usePortletSelector(prop('config'));

    return useMemo(() => {
        const getTKey = part => `${tKeyPrefix}.validation.${part}`;
        const defaultRules = getDefaultRules(getTKey);
        const allowedRules = pick(getAllowedLimits(config), defaultRules);
        const allowedTimeLimits = getAllowedTimeLimits(config);

        return mapObjIndexed((rules, fieldName) =>
            fieldName === loginCountName
                ? rules()
                : [
                    allowedTimeLimits.length === 1 || config.loginTimeLimitsMandatory
                        ? required({ predicate: isNotEmptyLimit, error: getTKey('limitsRequired') })
                        : oneOfBy({
                            fields: allowedTimeLimits,
                            predicate: isNotEmptyLimit,
                            error: getTKey('atLeastOneLimit')
                        }),
                    ...rules(allowedTimeLimits)
                ],
            allowedRules
        );
    }, [config]);
};
